import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { Dialog } from 'primeng/dialog';
import { Checkbox } from 'primeng/checkbox';
import { InputText } from 'primeng/inputtext';
import { Tooltip } from 'primeng/tooltip';
import { Tag } from 'primeng/tag';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { forkJoin, catchError, of, Observable } from 'rxjs';
import { OnboardingService } from '../../services/onboarding.service';
import { DesignationService } from '../../services/designation.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { EmployeeService } from '../../services/employee.service';
import { AuthService } from '../../services/auth.service';
import { OnboardingRecord, OnboardingTask, WelcomePackItem, OnboardingCategory } from '../../models/onboarding.model';
import { Employee, entraIdGroupLabel } from '../../models/employee.model';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { EmployeeFormDialogComponent, EmployeeSavedEvent } from '../employee-form-dialog/employee-form-dialog.component';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface DropdownOption { label: string; value: string; }

@Component({
  selector: 'app-onboarding-employee-list',
  standalone: true,
  imports: [
    CommonModule, FormsModule, TableModule, Button, Select, MultiSelect,
    Dialog, Checkbox, InputText, Tooltip, Tag, ConfirmDialog,
    EmployeeFormDialogComponent
  ],
  providers: [ConfirmationService],
  templateUrl: './onboarding-employee-list.component.html',
  styleUrl: './onboarding-employee-list.component.scss'
})
export class OnboardingEmployeeListComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  records: OnboardingRecord[] = [];
  filteredRecords: OnboardingRecord[] = [];
  loading = true;
  searchText = '';
  selectedStatusFilter: string | null = 'InProgress';
  selectedDesignationFilter: string[] | null = null;
  selectedDepartmentFilter: string[] | null = null;

  designationOptions: DropdownOption[] = [];
  departmentOptions: DropdownOption[] = [];

  rows = 15;
  first = 0;
  totalRecords = 0;
  rowsPerPageOptions = [15, 30, 50, 100];

  get showPaginator(): boolean { return this.totalRecords >= 15; }

  statusFilterOptions = [
    { label: 'All', value: null },
    { label: 'In Progress', value: 'InProgress' },
    { label: 'Completed', value: 'Completed' }
  ];

  // Employee form dialog (shared — used for both Add and Edit)
  displayEmployeeFormDialog = false;
  employeeToEdit: Employee | null = null;

  // View employee details dialog
  displayViewDialog = false;
  viewingEmployee: Employee | null = null;

  /** Template-facing wrapper for the shared Entra ID group label helper. */
  entraIdGroupLabel = entraIdGroupLabel;
  loadingEmployeeDetails = false;

  // Task management dialog
  displayTaskDialog = false;
  selectedRecord: OnboardingRecord | null = null;
  dialogTasks: OnboardingTask[] = [];
  dialogWelcomePackItems: WelcomePackItem[] = [];
  dialogFilterCategory: string | null = null;

  // User-defined category order/labels/icons (shared with the templates page). Drives the
  // category display order + labels + icons in the employee task dialog for consistency.
  categories: OnboardingCategory[] = [];
  private catOrder: Record<string, number> = {};
  private catLabel: Record<string, string> = {};
  private catIcon: Record<string, string> = {};
  savingTasks = false;
  dialogWorkEmail = '';
  private initialDialogWorkEmail = '';

  // T-shirt options for welcome pack
  tShirtTypes = [
    { label: 'Male', value: 'Male' },
    { label: 'Female', value: 'Female' }
  ];
  tShirtSizes = [
    { label: 'XS', value: 'XS' }, { label: 'S', value: 'S' },
    { label: 'M', value: 'M' }, { label: 'L', value: 'L' },
    { label: 'XL', value: 'XL' }, { label: 'XXL', value: 'XXL' }
  ];

  constructor(
    private onboardingService: OnboardingService,
    private designationService: DesignationService,
    private serviceGroupService: ServiceGroupService,
    private employeeService: EmployeeService,
    public authService: AuthService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit(): void {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by name, department...');
    this.pageHeaderService.setPrimaryButtonLabel('Add Employee');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(false);

    this.searchText = this.pageHeaderService.currentSearchText;
    this.loadCategories();
    this.loadData();

    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(v => { this.searchText = v ?? ''; this.applyFilters(); });

    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.exportToExcel());

    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.openAddEmployeeDialog());

    // Set button visibility immediately from current user, then keep in sync
    const currentUser = this.authService.getUser();
    this.pageHeaderService.setCanShowPrimaryAction(!!(currentUser?.isAdmin || currentUser?.isPc));
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => this.pageHeaderService.setCanShowPrimaryAction(!!(user?.isAdmin || user?.isPc)));
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadData(): void {
    this.loading = true;
    const statusParam = this.selectedStatusFilter ?? undefined;

    forkJoin({
      records: this.onboardingService.getAllRecords(statusParam).pipe(catchError(() => of([] as OnboardingRecord[]))),
      designations: this.designationService.getAllDesignations().pipe(catchError(() => of([]))),
      serviceGroups: this.serviceGroupService.getAllServiceGroups().pipe(catchError(() => of([])))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.records = data.records || [];
        this.designationOptions = (data.designations || []).filter((d: any) => d.isActive).map((d: any) => ({ label: d.name, value: d.name }));
        this.departmentOptions = (data.serviceGroups || []).filter((sg: any) => sg.isActive).map((sg: any) => ({ label: sg.name, value: sg.name }));
        this.applyFilters();
        this.loading = false;
      }
    });
  }

  // Lightweight refresh — only reloads records, not dropdowns
  refreshRecords(): void {
    const statusParam = this.selectedStatusFilter ?? undefined;
    this.onboardingService.getAllRecords(statusParam)
      .pipe(takeUntilDestroyed(this.destroyRef), catchError(() => of([] as OnboardingRecord[])))
      .subscribe(data => {
        this.records = data || [];
        this.applyFilters();
      });
  }

  applyFilters(): void {
    let filtered = [...this.records];
    if (this.searchText.trim()) {
      const s = this.searchText.toLowerCase();
      filtered = filtered.filter(r => r.employeeName?.toLowerCase().includes(s) || r.departmentName?.toLowerCase().includes(s) || r.designationName?.toLowerCase().includes(s));
    }
    if (this.selectedStatusFilter !== null) filtered = filtered.filter(r => r.status === this.selectedStatusFilter);
    if (this.selectedDesignationFilter?.length) filtered = filtered.filter(r => this.selectedDesignationFilter!.includes(r.designationName || ''));
    if (this.selectedDepartmentFilter?.length) filtered = filtered.filter(r => this.selectedDepartmentFilter!.includes(r.departmentName || ''));
    this.filteredRecords = filtered;
    this.totalRecords = filtered.length;
    this.first = 0;
  }

  onStatusFilterChange(): void { this.loadData(); }
  clearFilters(): void { this.selectedStatusFilter = null; this.selectedDesignationFilter = null; this.selectedDepartmentFilter = null; this.loadData(); }
  hasActiveFilters(): boolean { return this.selectedStatusFilter !== null || !!(this.selectedDesignationFilter?.length) || !!(this.selectedDepartmentFilter?.length); }

  // ===== Task Dialog =====

  openTaskDialog(record: OnboardingRecord, category?: string): void {
    // Reload record with full details
    this.onboardingService.getRecordById(record.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fullRecord) => {
          this.selectedRecord = fullRecord;
          this.dialogTasks = [...fullRecord.tasks].sort((a, b) => a.sortOrder - b.sortOrder);
          this.dialogWelcomePackItems = [...(fullRecord.welcomePackItems || [])];
          this.dialogFilterCategory = category || this.restrictedCategory || null;
          this.dialogWorkEmail = fullRecord.employeeEmail || '';
          this.initialDialogWorkEmail = this.dialogWorkEmail;
          this.displayTaskDialog = true;
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load record details' });
        }
      });
  }

  closeTaskDialog(): void {
    this.displayTaskDialog = false;
    this.selectedRecord = null;
    this.dialogWorkEmail = '';
    this.initialDialogWorkEmail = '';
  }

  syncingTasks = false;

  // Backfill this record with any default-template tasks it's missing (old/incomplete records).
  syncTasks(): void {
    if (!this.selectedRecord) return;
    this.syncingTasks = true;
    const recordId = this.selectedRecord.id;
    this.onboardingService.syncTasksFromTemplates(recordId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.syncingTasks = false;
          if (res.added > 0) {
            // Reload the record so the new tasks appear in the dialog.
            this.onboardingService.getRecordById(recordId)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(full => {
                this.dialogTasks = [...full.tasks].sort((a, b) => a.sortOrder - b.sortOrder);
              });
            this.messageService.add({ severity: 'success', summary: 'Tasks synced', detail: `${res.added} task(s) added from templates.` });
          } else {
            this.messageService.add({ severity: 'info', summary: 'Up to date', detail: 'No missing template tasks.' });
          }
        },
        error: () => {
          this.syncingTasks = false;
          this.messageService.add({ severity: 'error', summary: 'Sync failed', detail: 'Could not sync tasks.' });
        }
      });
  }

  getFilteredTasks(): OnboardingTask[] {
    if (!this.dialogFilterCategory) return this.dialogTasks;
    if (this.dialogFilterCategory === 'Orientation') {
      return this.dialogTasks.filter(t => t.category === 'Orientation' || t.category === 'OrientationType');
    }
    return this.dialogTasks.filter(t => t.category === this.dialogFilterCategory);
  }

  // Load the user-defined category order/labels/icons (falls back to built-ins if unavailable).
  loadCategories(): void {
    this.onboardingService.getCategories()
      .pipe(takeUntilDestroyed(this.destroyRef), catchError(() => of([] as OnboardingCategory[])))
      .subscribe(cats => {
        this.categories = cats || [];
        this.catOrder = {};
        this.catLabel = {};
        this.catIcon = {};
        for (const c of this.categories) {
          this.catOrder[c.key] = c.sortOrder + (c.isTab ? 0 : 100); // sub-cats after their notion of order
          if (c.label) this.catLabel[c.key] = c.label;
          if (c.icon) this.catIcon[c.key] = c.icon.startsWith('pi ') ? c.icon : `pi ${c.icon}`;
        }
      });
  }

  private readonly fallbackLabel: Record<string, string> = {
    'IT': 'IT — System & Access Setup',
    'PnC.Day1': 'Welcoming New Joiner — Day 1',
    'PnC.Communication': 'Communication & Engagement',
    'PnC.Documentation': 'Documentation & Compliance',
    'PnC.Systems': 'System & Access Setup',
    'PnC.Tracking': 'Tracking & Reviews',
    'LnD': 'L&D — Learning & Development',
    'Orientation': 'Orientation'
  };

  private readonly fallbackIcon: Record<string, string> = {
    'IT': 'pi pi-desktop',
    'PnC.Day1': 'pi pi-heart',
    'PnC.Communication': 'pi pi-comments',
    'PnC.Documentation': 'pi pi-file',
    'PnC.Systems': 'pi pi-cog',
    'PnC.Tracking': 'pi pi-chart-line',
    'LnD': 'pi pi-book',
    'Orientation': 'pi pi-compass'
  };

  getCategoryLabel(category: string): string {
    return this.catLabel[category] || this.fallbackLabel[category] || category;
  }

  getCategoryIcon(category: string): string {
    return this.catIcon[category] || this.fallbackIcon[category] || 'pi pi-list';
  }

  // Category display order in the dialog — driven by the user-defined category order when loaded,
  // otherwise the built-in default sequence.
  private readonly defaultCategorySequence =
    ['IT', 'PnC.Day1', 'PnC.Communication', 'PnC.Documentation', 'PnC.Systems', 'PnC.Tracking', 'LnD', 'Orientation'];

  private orderedSequence(keys: string[]): string[] {
    return [...keys].sort((a, b) =>
      (this.catOrder[a] ?? this.defaultCategorySequence.indexOf(a)) -
      (this.catOrder[b] ?? this.defaultCategorySequence.indexOf(b)));
  }

  get taskCategories(): string[] {
    if (this.dialogFilterCategory) {
      // If filtering by PnC, show all P&C sub-categories in the user-defined order.
      if (this.dialogFilterCategory === 'PnC') {
        return this.orderedSequence(['PnC.Day1', 'PnC.Communication', 'PnC.Documentation', 'PnC.Systems', 'PnC.Tracking']);
      }
      return [this.dialogFilterCategory];
    }
    return this.orderedSequence(this.defaultCategorySequence);
  }

  isCategoryAllDone(category: string): boolean {
    const tasks = this.getTasksByCategory(category);
    return tasks.length > 0 && tasks.every(t => t.isCompleted);
  }

  toggleCategory(category: string, checked: boolean): void {
    this.getTasksByCategory(category).forEach(t => t.isCompleted = checked);
    // Also toggle welcome pack items if it's the Day1 category
    if (category === 'PnC.Day1' && this.dialogWelcomePackItems.length > 0) {
      this.dialogWelcomePackItems.forEach(item => item.isProvided = checked);
    }
  }

  getTasksByCategory(category: string): OnboardingTask[] {
    return this.dialogTasks.filter(t => t.category === category).sort((a, b) => a.sortOrder - b.sortOrder);
  }

  saveTasks(): void {
    if (!this.selectedRecord) return;

    const trimmedEmail = this.dialogWorkEmail.trim();
    const emailChanged = trimmedEmail !== this.initialDialogWorkEmail.trim();

    // Guard: validate email format before hitting the API
    if (emailChanged && trimmedEmail && !this.isValidEmail(trimmedEmail)) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid Email', detail: 'Please enter a valid work email address.' });
      return;
    }

    this.savingTasks = true;
    const recordId = this.selectedRecord.id;
    const taskUpdates = this.dialogTasks.map(t => ({ id: t.id, isCompleted: t.isCompleted }));

    const saves: Observable<any>[] = [this.onboardingService.updateTasks(recordId, taskUpdates)];
    if (this.dialogWelcomePackItems.length > 0) {
      saves.push(this.onboardingService.updateWelcomePack(recordId, this.dialogWelcomePackItems));
    }
    if (emailChanged) {
      saves.push(this.onboardingService.setWorkEmail(recordId, trimmedEmail));
    }

    forkJoin(saves).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        if (emailChanged && this.selectedRecord) {
          this.selectedRecord.employeeEmail = trimmedEmail || undefined;
        }
        this.onSaveComplete(emailChanged);
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save tasks' });
        this.savingTasks = false;
      }
    });
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private onSaveComplete(emailChanged = false): void {
    this.savingTasks = false;
    const detail = emailChanged ? 'Tasks and work email updated successfully' : 'Tasks updated successfully';
    this.messageService.add({ severity: 'success', summary: 'Saved', detail });
    this.closeTaskDialog();
    this.refreshRecords();
  }

  // ===== View Employee Details =====

  viewEmployeeDetails(record: OnboardingRecord): void {
    this.viewingEmployee = null;
    this.loadingEmployeeDetails = true;
    this.displayViewDialog = true;
    this.employeeService.getEmployee(record.employeeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (emp) => { this.viewingEmployee = emp; this.loadingEmployeeDetails = false; },
        error: () => { this.loadingEmployeeDetails = false; this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load employee details' }); }
      });
  }

  closeViewDialog(): void {
    this.displayViewDialog = false;
    this.viewingEmployee = null;
  }

  // ===== Employee Form Dialog (Add / Edit) =====

  openAddEmployeeDialog(): void {
    this.employeeToEdit = null;
    this.displayEmployeeFormDialog = true;
  }

  editEmployee(record: OnboardingRecord): void {
    if (this.viewingEmployee && this.viewingEmployee.id === record.employeeId) {
      // Already have the employee from the view dialog — reuse it
      this.employeeToEdit = this.viewingEmployee;
      this.displayViewDialog = false;
      this.viewingEmployee = null;
      this.displayEmployeeFormDialog = true;
    } else {
      // Fetch employee data first, then open in edit mode
      this.employeeService.getEmployee(record.employeeId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (emp) => {
            this.employeeToEdit = emp;
            this.displayEmployeeFormDialog = true;
          },
          error: () => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load employee data' });
          }
        });
    }
  }

  editEmployeeFromView(): void {
    if (!this.viewingEmployee) return;
    this.employeeToEdit = this.viewingEmployee;
    this.displayViewDialog = false;
    this.viewingEmployee = null;
    this.displayEmployeeFormDialog = true;
  }

  onEmployeeSaved(event: EmployeeSavedEvent): void {
    this.displayEmployeeFormDialog = false;

    // The form dialog creates the onboarding record itself when the checkbox is ticked, so this
    // only reports the outcome — calling createRecord again here would make a second record.
    if (event.isNew) {
      this.messageService.add(event.startOnboarding
        ? { severity: 'success', summary: 'Onboarding Started', detail: `Onboarding started for ${event.employee.name}` }
        : { severity: 'success', summary: 'Employee Added', detail: `${event.employee.name} was added without an onboarding checklist.` });
    }

    this.refreshRecords();
  }

  onEmployeeFormCancel(): void {
    this.displayEmployeeFormDialog = false;
    this.employeeToEdit = null;
  }

  getRecordForEmployee(employeeId: string): OnboardingRecord {
    return this.records.find(r => r.employeeId === employeeId) ?? this.records[0];
  }

  openCvUrl(url: string): void {
    window.open(url, '_blank');
  }

  // ===== Table Actions =====

  deleteRecord(record: OnboardingRecord): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the onboarding record for "${record.employeeName}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.onboardingService.deleteRecord(record.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Record deleted' });
              this.refreshRecords();
            },
            error: () => {
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete' });
            }
          });
      }
    });
  }

  // Table column email helpers
  isEmailSent(record: OnboardingRecord, taskName: string): boolean {
    return record.tasks?.some(t => t.taskName === taskName && t.isCompleted) ?? false;
  }

  sendEmail(record: OnboardingRecord, _taskName: string, templateType: string): void {
    this.confirmationService.confirm({
      message: `Send ${templateType} email to "${record.employeeName}"?`,
      header: 'Send Email',
      icon: 'pi pi-envelope',
      accept: () => {
        this.onboardingService.sendEmail(record.id, templateType)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.messageService.add({ severity: 'success', summary: 'Sent', detail: `${templateType} email sent to ${record.employeeName}` });
              this.refreshRecords();
            },
            error: (err) => this.showEmailError(err)
          });
      }
    });
  }

  /**
   * Pulls the server's explanation out of a failed send-email response.
   * <p>
   * The onboarding send-email call uses responseType 'text', so a 422 body arrives as a JSON
   * STRING rather than a parsed object — reading err.error.message returns undefined and the raw
   * `{"message":"..."}` was shown to the user. Parse it back before falling through.
   * </p>
   */
  private extractEmailError(err: unknown): { message: string; notificationDisabled: boolean } {
    const body = (err as { error?: unknown })?.error;
    const fallback = 'No email address found for this employee. Please add a work or personal email in Employee Management.';

    let parsed: { message?: string; notificationDisabled?: boolean } | null = null;

    if (body && typeof body === 'object') {
      parsed = body as { message?: string; notificationDisabled?: boolean };
    } else if (typeof body === 'string' && body.trim()) {
      try {
        parsed = JSON.parse(body);
      } catch {
        // Not JSON — a plain-text body is already the message.
        return { message: body, notificationDisabled: false };
      }
    }

    return {
      message: parsed?.message || fallback,
      notificationDisabled: parsed?.notificationDisabled === true
    };
  }

  /**
   * Toast for a send-email failure. A notification switched off by an admin is a warning, not an
   * error — nothing malfunctioned, so it reads amber like the work-log reminder. Genuine problems
   * (no address, no template, Graph failure) stay red.
   */
  private showEmailError(err: unknown): void {
    const { message, notificationDisabled } = this.extractEmailError(err);

    this.messageService.add({
      severity: notificationDisabled ? 'warn' : 'error',
      summary: notificationDisabled ? 'Email Not Sent' : 'Cannot Send Email',
      detail: message,
      life: 8000
    });
  }

  // ===== Role-based access helpers =====

  /** Returns the single category an IT/L&D user is restricted to, or null for admin/pc (full access). */
  get restrictedCategory(): string | null {
    const user = this.authService.getUser();
    if (!user) return null;
    if (user.isAdmin || user.isPc) return null;
    if (user.isIt) return 'IT';
    if (user.isLnD) return 'LnD';
    return null;
  }

  get isAdminOrPc(): boolean {
    const user = this.authService.getUser();
    return !!(user?.isAdmin || user?.isPc);
  }

  /** True when user is IT-only (not admin/pc) — shows IT column only. */
  get isItOnly(): boolean {
    const user = this.authService.getUser();
    return !!(user?.isIt && !user?.isAdmin && !user?.isPc);
  }

  /** True when user is L&D-only (not admin/pc) — shows L&D column only. */
  get isLnDOnly(): boolean {
    const user = this.authService.getUser();
    return !!(user?.isLnD && !user?.isAdmin && !user?.isPc);
  }

  // ===== Task dialog email icon helpers — use emailType field set on each task =====

  /** True for the IT task about adding the joiner to a Bistec Apex Entra ID group.
   *  Matched on keywords, not an exact name: this task is created by admins in Task
   *  Templates, so its wording differs between records. */
  isEntraGroupTask(task: OnboardingTask): boolean {
    const n = task.taskName.toLowerCase();
    return n.includes('entra') && n.includes('group');
  }

  isEmailTask(task: OnboardingTask): boolean {
    return !!task.emailType;
  }

  getEmailTypeLabel(emailType: string): string {
    switch (emailType) {
      case 'Welcome': return 'Welcome Email';
      case 'AllHearts': return 'All-Hearts Email';
      case 'Onboarding': return 'Onboarding Email';
      default: return emailType;
    }
  }

  sendEmailForTask(task: OnboardingTask): void {
    if (!this.selectedRecord || task.isCompleted || !task.emailType) return;
    const templateType = task.emailType;
    const label = this.getEmailTypeLabel(templateType);
    this.confirmationService.confirm({
      message: `Send ${label} to "${this.selectedRecord.employeeName}"?`,
      header: 'Send Email',
      icon: 'pi pi-envelope',
      accept: () => {
        this.onboardingService.sendEmail(this.selectedRecord!.id, templateType)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              task.isCompleted = true;
              this.messageService.add({ severity: 'success', summary: 'Sent', detail: `${label} sent to ${this.selectedRecord!.employeeName}` });
            },
            error: (err) => this.showEmailError(err)
          });
      }
    });
  }

  // Work email gate helpers
  hasWorkEmail(record: OnboardingRecord): boolean {
    return !!(record.employeeEmail?.trim());
  }

  canSendEmailTask(task: OnboardingTask): boolean {
    if (task.emailType === 'Welcome') {
      // Allow if already saved OR freshly entered in the dialog
      return !!(this.selectedRecord?.employeeEmail?.trim()) || !!(this.dialogWorkEmail?.trim());
    }
    return true;
  }

  exportToExcel(): void {
    if (!this.filteredRecords.length) { this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'No records to export' }); return; }
    const data = this.filteredRecords.map(r => ({
      'Name': r.employeeName, 'Designation': r.designationName || '-', 'Department': r.departmentName || '-',
      'Joining Date': this.formatDate(r.joiningDate), 'IT': r.itStatus,
      'P&C': r.pnCStatus, 'L&D': r.lnDStatus, 'Orientation': r.orientationStatus,
      'Welcome Email': this.isEmailSent(r, 'Welcome Email Send') ? 'Sent' : 'Pending',
      'Onboarding Email': this.isEmailSent(r, 'Send Onboarding Email') ? 'Sent' : 'Pending',
      'Status': r.status
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = { Sheets: { 'Onboarding': ws }, SheetNames: ['Onboarding'] };
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Onboarding_Employees_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  getTaskSeverity(status: string): 'success' | 'warn' { return (status === 'Done' || status === 'Completed') ? 'success' : 'warn'; }
  getTaskLabel(status: string): string { return (status === 'Done' || status === 'Completed') ? 'Done' : 'Pending'; }

  formatDate(dateStr?: string): string { if (!dateStr) return '-'; try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return dateStr; } }
}
